import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

const PROJECT_NAME = "On-Brand Picture Generator";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/home");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand side panel */}
      <div className="bg-primary hidden flex-col justify-between p-10 lg:flex lg:w-[45%]">
        <BrandLogo name={PROJECT_NAME} className="h-8 brightness-0 invert" />
        <div className="space-y-4">
          <h1 className="text-3xl text-white">Generate visuals that stay unmistakably on-brand</h1>
          <p className="max-w-sm text-base leading-relaxed text-white/60">
            Build structured prompts from BOI photography templates, then generate images aligned
            with our visual identity.
          </p>
        </div>
        <p className="text-xs text-white/30">Board of Innovation &mdash; Internal Tool</p>
      </div>

      {/* Login form */}
      <div className="bg-background flex flex-1 items-center justify-center px-6 py-10">
        <Card className="border-border w-full max-w-md border">
          <CardHeader className="space-y-4 text-center">
            <div className="bg-primary mx-auto flex h-16 w-16 items-center justify-center rounded-md">
              <BrandLogo name={PROJECT_NAME} className="h-7 brightness-0 invert" />
            </div>
            <div className="space-y-1">
              <p className="text-foreground text-xl">Welcome back</p>
              <p className="text-muted text-sm">Sign in to generate on-brand imagery.</p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@boardofinnovation.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                />
                <label htmlFor="rememberMe" className="text-foreground text-sm">
                  Remember me for 30 days
                </label>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button
                type="submit"
                disabled={loginMutation.isPending}
                className="bg-accent text-accent-foreground w-full hover:bg-[#0E0E98]"
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <p className="text-muted text-center text-sm">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-accent font-medium hover:underline">
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
